import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { parseQueryString } from "app/utils";

const QueryStringContext = React.createContext({
  queryString: {},
  onClearQueryString: undefined,
  onToggleQueryStringValue: (key, id, isEventType, useQueryCached) => {},
  onUpdateQueryString: undefined
});

export const QueryStringConsumer = QueryStringContext.Consumer;

const makeQueryString = object => {
  const toKeyValuePair = key => object[key] && `${key}=${object[key]}`;
  const keyValuePairs = Object.keys(object).sort().map(toKeyValuePair).filter(Boolean);

  if (keyValuePairs.length === 0) {
    return "";
  }

  return `?${keyValuePairs.join("&")}`;
};

const parserTypes = {
  intList: value => value.split(",").map(int => parseInt(int, 10)),
  string: value => value,
  stringList: value => value.split(",")
};

export const QueryStringProvider = ({ children, parsers }) => {
  const history = useHistory();

  const value = useMemo(
    () => {
      const queryString = parseQueryString(history.location.search);

      Object.keys(queryString).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(parsers, key)) {
          queryString[key] = parserTypes[parsers[key]](queryString[key]);
        }
      });

      const onClearQueryString = () => {
        history.push(history.location.pathname);
      };

      const onUpdateQueryString = mutator => {
        const nextQueryString = makeQueryString(mutator(queryString));
        history.push(`${history.location.pathname}${nextQueryString}`);
      };

      const onClearQueryStringKey = key => {
        onUpdateQueryString(({ [key]: list, ...current }) => current);
      };

      const onToggleQueryStringValue = (key, id, isEventType = false, useQueryCached = true) => {
        const query = useQueryCached ? queryString : {};
        let cleanedQueryString = {};
        if (isEventType) {
          if (query[key]) {
            cleanedQueryString[key] = query[key].map(actualUri => decodeURI(actualUri));
          }
        } else {
          cleanedQueryString = query;
        }
        if (cleanedQueryString[key] && cleanedQueryString[key].includes(id)) {
          let list = cleanedQueryString[key].filter(current => current !== id);
          list = list.length > 0 ? list.join(",") : null;
          onUpdateQueryString(current => ({ ...current, [key]: list }));
        } else {
          const list = [...(cleanedQueryString[key] || []), id].sort().join(",");
          onUpdateQueryString(current => ({ ...current, [key]: list }));
        }
      };

      return {
        queryString,
        onClearQueryString,
        onClearQueryStringKey,
        onToggleQueryStringValue,
        onUpdateQueryString
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history.location.search, history.location.pathname]
  );

  return (
    <QueryStringContext.Provider value={value}>
      {children}
    </QueryStringContext.Provider>
  );
};

export const useQueryString = () => useContext(QueryStringContext);
